import { PostApi } from "@/apis/v2/PostApi";
import { CustomBackdropController } from "@/components/backdrop/default/v1";
import { customToast } from "@/components/toast/custom-react-toastify/v1";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const QUERY_KEY = 'Posts';
const PAGE_SIZE = 50;

function handleError(error) {
    if (!error?.response) {
        customToast.error('서버와의 연결이 원활하지 않습니다.');
        return;
    }

    customToast.error(error.response?.data?.message);
}

// 내 글 목록 (임시저장 포함, 최근 수정순). 첫 페이지에 groups·ungrouped 가 온다
export function useMyPostsQuery({ userPageId }) {
    return useInfiniteQuery({
        queryKey: [QUERY_KEY, 'list', userPageId],
        enabled: !!userPageId,
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            const response = await PostApi.getPosts({ params: { userPageId, scope: 'OWNER', offset: pageParam, limit: PAGE_SIZE } });

            return response?.data?.data;
        },
        getNextPageParam: (lastPage) => {
            const nextOffset = lastPage.offset + lastPage.items.length;
            return nextOffset < lastPage.total ? nextOffset : undefined;
        },
    });
}

// 수정할 글 (본문 포함). 다른 화면에서 저장했을 수 있어 열 때마다 새로 받는다
export function usePostQuery({ id }) {
    return useQuery({
        queryKey: [QUERY_KEY, 'one', id],
        enabled: !!id,
        staleTime: 0,
        gcTime: 0,
        queryFn: async () => {
            const response = await PostApi.getPost({ id });

            return response?.data?.data;
        },
    });
}

function usePostMutation(mutationFn) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (variables) => {
            const response = await mutationFn(variables);

            return response?.data?.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'list'] });
        },
        onError: handleError,
        onMutate: async () => {
            CustomBackdropController().showBackdrop();
        },
        onSettled: async () => {
            CustomBackdropController().hideBackdrop();
        }
    });
}

export function useCreatePostMutation() {
    return usePostMutation(({ body }) => PostApi.createPost({ body }));
}

export function useUpdatePostMutation() {
    return usePostMutation(({ id, body }) => PostApi.updatePost({ id, body }));
}

export function useDeletePostMutation() {
    return usePostMutation(({ id }) => PostApi.deletePost({ id }));
}
